import { useState, useEffect, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { captureError } from '@/lib/sentry';
import { resolveCampaignTotals, describeCampaignWindow, type DurableCampaignCounters } from '@/lib/email/campaign-totals';
import { computeCampaignJobCounts } from '@/lib/email/job-status';

interface QueueJob {
  id: string;
  recipient_email: string;
  recipient_data: {
    first_name?: string;
    last_name?: string;
    company?: string;
  } | null;
  status: string;
  scheduled_send_time: string | null;
  sent_at: string | null;
  attempts: number;
  error_message: string | null;
}

interface HermesCampaignContactsProps {
  campaignId: string;
  /**
   * Durable counters from the campaign row. Totals come from here, never from surviving
   * queue rows, which are purged 7 days after sending (#859). Optional so the component
   * still renders without it.
   */
  campaign?: DurableCampaignCounters;
}

export function HermesCampaignContacts({ campaignId, campaign }: HermesCampaignContactsProps) {
  const [jobs, setJobs] = useState<QueueJob[]>([]);
  const [loading, setLoading] = useState(true);
  // #859: a failed fetch also leaves `jobs` empty. Without this flag the empty list is
  // indistinguishable from a purged window and we would confidently tell the user their
  // records were removed while a "failed to load" toast is still on screen.
  const [loadFailed, setLoadFailed] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchRecipients = async () => {
      try {
        setLoading(true);
        setLoadFailed(false);
        const { data, error } = await supabase
          .from('hermes_email_queue_jobs')
          .select('id, recipient_email, recipient_data, status, scheduled_send_time, sent_at, attempts, error_message')
          .eq('campaign_id', campaignId)
          .order('scheduled_send_time', { ascending: true });

        if (error) throw error;
        setJobs(data || []);
      } catch (error) {
        console.error('Error fetching campaign recipients:', error);
        setLoadFailed(true);
        captureError(error as Error, { feature: 'campaigns-hermes-campaign-contacts-fetch-recipients' });
        toast({
          title: "Error",
          description: "Failed to load campaign recipients",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchRecipients();
  }, [campaignId, toast]);

  // #859: totals must come from the durable campaign row, not from `jobs` — queue rows are
  // purged 7 days after sending, so a jobs.length-derived count can decay to zero even when
  // the campaign has real recipients. The window is only handed over once the fetch has
  // actually succeeded; a loading or failed fetch knows nothing about what was purged.
  const windowSettled = !loading && !loadFailed;
  const totals = useMemo(
    () => resolveCampaignTotals(campaign ?? {}, windowSettled ? computeCampaignJobCounts(jobs) : null),
    [campaign, jobs, windowSettled],
  );
  const windowNotice = describeCampaignWindow(totals);

  const statusVariant = (status: string) => {
    switch (status) {
      case 'sent': return 'default';
      case 'pending': return 'secondary';
      case 'failed': return 'destructive';
      default: return 'outline';
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading recipients...</div>;
  }

  if (loadFailed) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        Couldn&rsquo;t load this campaign&rsquo;s recipients. Try again in a moment.
      </div>
    );
  }

  if (jobs.length === 0) {
    // #859: an empty queue-job window doesn't mean zero recipients — `sent` rows are purged 7
    // days after sending. Only claim "no recipients" when the durable total agrees; that also
    // covers `campaign` not being passed, since totals.recipients then reads as 0.
    if (totals.recipients === 0) {
      return (
        <div className="text-center py-8 text-muted-foreground">
          No recipients in this campaign
        </div>
      );
    }
    return <div className="text-center py-8 text-muted-foreground">{windowNotice}</div>;
  }

  // Without a campaign row there is no durable total, so the surviving rows are all we know.
  const headerCount = campaign ? totals.recipients : jobs.length;

  return (
    <div className="space-y-4">
      <div className="text-sm text-muted-foreground">
        {headerCount} recipient{headerCount !== 1 ? 's' : ''}
        {windowNotice && <div className="text-xs text-muted-foreground mt-1">{windowNotice}</div>}
      </div>
      <div className="border rounded-lg divide-y">
        {jobs.map((job) => (
          <div
            key={job.id}
            className="flex items-center justify-between p-4 hover:bg-accent/50 transition-colors"
          >
            <div>
              <div className="font-medium">
                {job.recipient_data?.first_name || ''} {job.recipient_data?.last_name || ''}
              </div>
              <div className="text-sm text-muted-foreground">
                {job.recipient_email}
                {job.recipient_data?.company && ` · ${job.recipient_data.company}`}
              </div>
              {job.error_message && (
                <div className="text-xs text-destructive mt-1">{job.error_message}</div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={statusVariant(job.status)}>
                {job.status}
              </Badge>
              {job.attempts > 0 && (
                <span className="text-xs text-muted-foreground">
                  {job.attempts} attempt{job.attempts !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
